/**
 * Which model ids are decisions models, and which agents may bind one. Pure: no
 * fetch, no storage.
 *
 * A decisions model answers closed-set questions and returns no text at all, so
 * binding one to an agent whose output is read as text produces an agent that
 * cannot answer. The two halves here are the predicate that recognises such a
 * model and the check that says whether a given agent's output shape survives it.
 *
 * The closed-set half is not decided here: `schemaToQuestions` in
 * `decision-questions.ts` already decides whether a `schema_json` maps to
 * questions, and this module asks it rather than re-deriving the answer, so a
 * schema a binding accepts is a schema the mapper can ask.
 */

import { schemaToQuestions, type DecisionQuestionRefusal } from './decision-questions';
import type { AgentRole } from './constants';
import { getConnectorById } from './catalog';

/**
 * Model-id prefixes that name a decisions model, matched case-insensitively.
 *
 * OpenRouter serves both forms (`typesafe/jev-1.13`, and the bare `jev-latest` —
 * it refuses `typesafe/jev-latest`, #4486); the bare prefix is also what a direct
 * TypeSafe connector would carry. Prefixes rather than an id list because the
 * OpenRouter model select accepts custom values, so a hand-typed successor has to
 * match without a catalog edit — and a flag on a catalog entry cannot reach a
 * model id that was never in the catalog.
 *
 * A match says the id names a decisions model, never that a provider serves it:
 * a hand-typed id the provider refuses matches too, and fails at the provider as
 * any hand-typed model id does. Which ids the catalog offers is the catalog's
 * claim, held to the served ids in `decisions-completion.test.ts`.
 *
 * Known limit: a Jev-compatible server published under an unrelated model id does
 * not match, and reaching it needs an explicit per-connection setting rather than
 * another prefix guess.
 */
const DECISIONS_MODEL_PREFIXES = ['typesafe/jev-', 'jev-'] as const;

/**
 * The form `isDecisionsModel` matches on: trimmed and lowercased. A lookup keyed
 * by decisions model id goes through this, so it matches every spelling the
 * predicate does.
 */
export function normalizeDecisionsModelId(modelId: string): string {
  return modelId.trim().toLowerCase();
}

/** Whether `modelId` names a decisions model. */
export function isDecisionsModel(modelId: unknown): boolean {
  if (typeof modelId !== 'string') return false;
  const normalized = normalizeDecisionsModelId(modelId);
  return DECISIONS_MODEL_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * The roles whose turn output is consumed as a JSON object rather than read or
 * delivered as text, and so the only roles a decisions model can serve.
 *
 * `summarizer` is absent although it too writes `json_schema`: `SUMMARIZER_SCHEMA`
 * is a `sections` array of free-text objects, which the schema check below refuses
 * on its own. Listing the role here would make a summarizer binding read as the
 * schema's problem when the role is what decides it.
 */
export const DECISION_CAPABLE_AGENT_ROLES: readonly AgentRole[] = [
  'monitor',
  'conversation_evaluator',
  'message_evaluator',
];

/** Whether `role` is one a decisions model can serve (`DECISION_CAPABLE_AGENT_ROLES`). */
export function isDecisionCapableRole(role: unknown): boolean {
  return (DECISION_CAPABLE_AGENT_ROLES as readonly unknown[]).includes(role);
}

/**
 * Whether a connector serves decisions models only — every model it offers is one
 * (`isDecisionsModel`), as TypeSafe's are. Such a connection runs no chat turn: only a
 * decision-capable role's turn runs on it (`decisionsOnlyConnectionRefusesRole`).
 */
export function isDecisionsOnlyConnector(connectorId: unknown): boolean {
  const models = typeof connectorId === 'string' ? getConnectorById(connectorId)?.models : null;
  return !!models && models.length > 0 && models.every((model) => isDecisionsModel(model));
}

/**
 * Whether an agent of `role` cannot run on a connection whose catalog connector is
 * `connectorId`: a decisions-only connection and a role that is not decision-capable
 * (`isDecisionCapableRole`), whose turn would be refused on it. What a surface that offers
 * connections to a role asks; no agent writer enforces it.
 */
export function decisionsOnlyConnectionRefusesRole(connectorId: unknown, role: unknown): boolean {
  return isDecisionsOnlyConnector(connectorId) && !isDecisionCapableRole(role);
}

/**
 * Why an agent may not bind a decisions model.
 *
 * `role_produces_text` — a role whose output is delivered or read as text.
 * `response_format_not_json_schema` — the agent is set to produce text.
 * `schema_missing` — `json_schema` with nothing to ask.
 * `schema_unreadable` — a `schema_json` string that is not JSON.
 * `schema_not_closed_set` — at least one field a decisions model has no answer
 *   shape for; `schema_refusals` names each one.
 */
export type DecisionsBindingRefusalReason =
  | 'role_produces_text'
  | 'response_format_not_json_schema'
  | 'schema_missing'
  | 'schema_unreadable'
  | 'schema_not_closed_set';

export interface DecisionsBindingRefusal {
  reason: DecisionsBindingRefusalReason;
  /** Populated for `schema_not_closed_set`; empty otherwise. */
  schema_refusals: DecisionQuestionRefusal[];
  /** One line naming the model and what about the agent refused it. */
  message: string;
}

/**
 * The agent columns a binding is decided from.
 *
 * A write carrying none of them leaves the binding exactly as it was, which is
 * what `touchesDecisionsBinding` exists to recognise.
 */
export const DECISIONS_BINDING_FIELDS = [
  'agent_settings',
  'agent_role',
  'response_format',
  'schema_json',
] as const;

/**
 * Whether an update payload can change an agent's binding.
 *
 * The rule is a SAVE-TIME rule: it refuses a binding as it is written. A rename,
 * a disable, an instructions upload and the `updated_at` touch a tool assignment
 * makes all reach `updateAgent` carrying none of these columns, so judging them
 * would refuse writes that cannot make the binding worse — and would leave an
 * agent hand-typed onto a decisions model before the rule existed impossible to
 * rename or turn off.
 */
export function touchesDecisionsBinding(input: Record<string, unknown>): boolean {
  return DECISIONS_BINDING_FIELDS.some((field) => input[field] !== undefined);
}

/** The agent fields a binding is decided from, in whatever form its writer holds them. */
export interface DecisionsBindingInput {
  /** The agent's effective model id — `agent_settings.model`. */
  model: unknown;
  agent_role: unknown;
  /** The `response_format` column: `text` or `json_schema`. */
  response_format: unknown;
  /** The `schema_json` column, as an object or as the JSON string a column holds. */
  schema_json: unknown;
}

/**
 * Whether this agent may bind this model. `null` means it may.
 *
 * An agent on any other model is never refused here: the check is about what a
 * decisions model can answer, so it returns `null` for every model the predicate
 * does not match, whatever the agent's shape.
 */
export function checkDecisionsModelBinding(
  agent: DecisionsBindingInput,
): DecisionsBindingRefusal | null {
  if (!isDecisionsModel(agent.model)) return null;
  const model = String(agent.model);

  const role = agent.agent_role;
  if (!isDecisionCapableRole(role)) {
    return refuse(
      'role_produces_text',
      `agent_role "${String(role)}" produces text, which the decisions model "${model}" does not return. ` +
        `A decisions model binds to: ${DECISION_CAPABLE_AGENT_ROLES.join(', ')}.`,
    );
  }

  if (agent.response_format !== 'json_schema') {
    return refuse(
      'response_format_not_json_schema',
      `response_format must be "json_schema" to bind the decisions model "${model}".`,
    );
  }

  if (agent.schema_json === null || agent.schema_json === undefined || agent.schema_json === '') {
    return refuse(
      'schema_missing',
      `schema_json is required to bind the decisions model "${model}": a decisions model answers questions, and the schema is where they come from.`,
    );
  }

  let schema: unknown = agent.schema_json;
  if (typeof schema === 'string') {
    try {
      schema = JSON.parse(schema);
    } catch {
      return refuse(
        'schema_unreadable',
        `schema_json is not valid JSON, so the decisions model "${model}" cannot be bound.`,
      );
    }
  }

  const mapped = schemaToQuestions(schema);
  if (mapped.ok) return null;

  const detail = mapped.refusals
    .map((refusal) => `${refusal.field ?? '(schema)'}: ${refusal.reason}`)
    .join('; ');
  return {
    reason: 'schema_not_closed_set',
    schema_refusals: mapped.refusals,
    message:
      `schema_json is not closed-set, so the decisions model "${model}" cannot answer it — ${detail}. ` +
      'A decisions model answers enum, boolean and bounded-integer fields only.',
  };
}

function refuse(reason: DecisionsBindingRefusalReason, message: string): DecisionsBindingRefusal {
  return { reason, schema_refusals: [], message };
}

/**
 * The model id an agent's stored `agent_settings` names, or null when it names
 * none.
 *
 * Accepts the object a REST body carries and the JSON string the column holds,
 * because the writers that gate a binding see both forms. Settings that name no
 * model leave the agent on its connector's own default, which this returns null
 * for.
 */
export function readAgentSettingsModel(agentSettings: unknown): string | null {
  let settings: unknown = agentSettings;
  if (typeof settings === 'string') {
    try {
      settings = JSON.parse(settings);
    } catch {
      return null;
    }
  }
  if (typeof settings !== 'object' || settings === null || Array.isArray(settings)) return null;
  const model = (settings as Record<string, unknown>).model;
  return typeof model === 'string' ? model : null;
}
